import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import usePageMeta from 'hooks/use-page-meta';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import LetterFilter from 'pages/Search/components/LetterFilter';
import RedirectLink from 'pages/Search/components/RedirectLink';
import SearchInput from 'pages/Search/components/SearchInput';
import SectionHeading from 'pages/Search/components/SectionHeading';
import { useVendorCatalogPayload } from 'pages/Search/components/Vendors/hooks/use-vendor-catalog-payload';
import { ROUTES } from 'pages/Search/constants';
import { normalizeName } from 'pages/Search/utils';
import { useVendorsCatalog } from 'queries/catalog/use-vendors-catalog';

import useStyles from './styles';

const Vendors: React.FC = () => {
    const { classes } = useStyles();

    const layoutRef = useRef<{ scrollTop: VoidFunction } | null>(null);
    const [payload, actions] = useVendorCatalogPayload();
    const { data, isLoading } = useVendorsCatalog(payload);

    useEffect(() => {
        if (data && layoutRef.current) {
            layoutRef.current.scrollTop();
        }
    }, [data]);

    const hasMetaPagination = payload.page > 1;
    const pageMeta = hasMetaPagination ? ' - Page ' + payload.page : '';

    usePageMeta({
        title: `Vulnerabilities by vendors${
            payload.letter ? ' - ' + payload.letter.toUpperCase() : ''
        }${pageMeta} | Vulners.com`,
        description: `Vulnerabilities by vendor names${
            payload.letter ? 'starting with' + payload.letter : ''
        }${pageMeta}`
    });

    const getOptionHref = useCallback((letter: string) => actions.url({ letter }), [actions]);
    const getPaginationHref = useCallback((page: number) => actions.url({ page }), [actions]);

    const tableHeaders = useMemo(
        () => [
            { field: 'vendor' as const, label: 'Vendor', sort: true },
            { field: 'products' as const, label: 'Products', sort: true, width: 120 },
            { field: 'cves' as const, label: 'Vulnerabilities', sort: true, width: 120 }
        ],
        []
    );

    const tableData = useMemo(
        () =>
            data?.items.map(({ vendor, products, cves }) => [
                <RedirectLink
                    key={`${vendor}-vulnerabilities`}
                    to={ROUTES.VENDOR({ page: null, vendor })}
                    label={normalizeName(vendor)}
                    active={!!vendor}
                    className={classes.link}
                />,
                products,
                cves
            ]) ?? [],
        [classes.link, data?.items]
    );

    const handlePageChange = useCallback((page: number) => actions.set({ page }), [actions]);
    const handleLetterSelect = useCallback((letter: string) => actions.set({ letter }), [actions]);
    const handlePageSizeChange = useCallback((size: number) => actions.set({ size }), [actions]);

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <div className={classes.content}>
                    <SectionHeading
                        title={['Vendors', payload.letter?.toUpperCase()]}
                        total={data?.total ?? NaN}
                        breadcrumbs={[
                            { to: ROUTES.VENDORS(), label: 'Vendors' },
                            payload.letter
                                ? {
                                      to: ROUTES.VENDORS({ letter: payload.letter }),
                                      label: payload.letter.toUpperCase()
                                  }
                                : null
                        ]}
                    />
                    <SearchInput
                        value={payload.search}
                        placeholder="Search by vendor name"
                        onChange={(value) => actions.set({ search: value ?? undefined })}
                    />
                    <LetterFilter
                        total={data?.total ?? NaN}
                        selected={payload.letter}
                        getOptionHref={getOptionHref}
                        onSelect={handleLetterSelect}
                    />
                    <DataTable
                        headers={tableHeaders}
                        data={tableData}
                        loading={isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        onSortingChange={(sort) => actions.set({ sort })}
                        page={payload.page}
                        pageSize={payload.size}
                        totalCount={data?.total}
                        sorting={payload.sort}
                    />
                </div>
            }
        />
    );
};

export default Vendors;
